import { showSuccessToast, showErrorToast } from '../../../components/Toast/Toaster';
import { useCodAutomationStore } from '../../../store/CodAutomation/codAutomationStore';
import SearchInput from '../../../components/dropdown/autoCompleteInputComponent';
import { useEffect } from 'react';
import { useFormik } from "formik";
import { object, string, number } from 'yup';

const BankChargesForm = ({ row, countriesData, currencyData, onSaveSuccess, onCancel }) => {

  const addBankCharges = useCodAutomationStore(state => state.addBankCharges);
  const editBankCharges = useCodAutomationStore(state => state.editBankCharges);
  const getBanksLookup = useCodAutomationStore(state => state.getBanksLookup);
  const banksLookupList = useCodAutomationStore(state => state.banksLookupList);

  useEffect(() => {
    getBanksLookup();
  }, [getBanksLookup]);

  const bankChargesFormik = useFormik({
    initialValues: {
      country: row?.countryCode || '',
      bankName: row?.bankName || '',
      currency: row?.currency || '',
      charges: row?.charges || '',
    },
    enableReinitialize: true,
    validationSchema: object({
      country: string().required("Country is required"),
      bankName: string().trim().required("Bank Name is required").min(1, "Bank Name is required"),
      currency: string().required("Currency is required"),
      charges: number().typeError("Charges must be a number").required("Charges is required").positive("Charges must be greater than zero"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { country, bankName, currency, charges } = values || {};
      const selectedBank = (banksLookupList || []).find(
        (item) => item?.key === bankName || item?.value === bankName || item?.label === bankName
      );

      const data = {
        countryCode: country,
        bankName: selectedBank?.key || bankName?.trim(),
        currency: currency,
        charges: charges,
      };

      try {
        let response;
        if (row) {
          response = await editBankCharges(currency, data);
        } else {
          response = await addBankCharges(data);
        }

        const responseData = response?.data || response?.response?.data;
        if (responseData?.status === "SUCCESS") {
          showSuccessToast(responseData?.msg || "Saved successfully");
          onSaveSuccess();
        } else {
          showErrorToast(responseData?.msg || "Error while saving data");
        }
      } catch (error) {
        showErrorToast("Error");
      }
    },
  });

  const { values, touched, errors, setFieldValue, handleBlur, handleSubmit } = bankChargesFormik || {};
    
  return (
    <div className="row">
      <div className="col-md-12" style={{ padding: "12px" }}>
          <div className="borderFramer">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="borderFrameTitle pickupTitle">
                Bank Charges
              </div>
            </div>
            <div className="row align-items-end mb-3">
              <div className="col-md-3 mb-2">
                <label className="fontSize14 menacingCloudsText fontWeight500 mb-1">
                  Country <span className='bloodDonortext fontWeight600 fontSize16'>*</span>
                </label>
                <SearchInput
                  id="country"
                  label="country"
                  name="country"
                  isClearable={true}
                  options={countriesData?.data?.data?.content || []}
                  getOptionLabel={(country) => country.name}
                  getOptionValue={(country) => country.isoCode3}
                  isSearchable={true}
                  placeholder="Select Country"
                  value={countriesData?.data?.data?.content?.find((item) => item?.isoCode3 === values?.country) || ''}
                  handleChange={(selected) => setFieldValue("country", selected?.isoCode3 ?? '')}
                  handleBlur={handleBlur}
                />
                <div style={{ minHeight: "18px" }}>
                  {touched?.country && errors?.country && (
                    <p className="requiredText">
                      {errors?.country}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-md-3 mb-2">
                <label className="fontSize14 menacingCloudsText fontWeight500 mb-1">
                  Bank Name <span className='bloodDonortext fontWeight600 fontSize16'>*</span>
                </label>
                <SearchInput
                  id="bankName"
                  label="bankName"
                  name="bankName"
                  isClearable={true}
                  options={banksLookupList || []}
                  getOptionLabel={(option) => option?.label || ''}
                  getOptionValue={(option) => option?.key || ''}
                  isSearchable={true}
                  placeholder="Select Bank"
                  value={(banksLookupList || []).find((item) => item?.key === values?.bankName || item?.label === values?.bankName || item?.value === values?.bankName) || ''}
                  handleChange={(selected) => setFieldValue("bankName", selected?.key || '')}
                  handleBlur={handleBlur}
                />
                <div style={{ minHeight: "18px" }}>
                  {touched?.bankName && errors?.bankName && (
                    <p className="requiredText">
                        {errors?.bankName}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-md-3 mb-2">
                <label className="fontSize14 menacingCloudsText fontWeight500 mb-1">
                  Currency <span className='bloodDonortext fontWeight600 fontSize16'>*</span>
                </label>
                <SearchInput
                  id="currency"
                  label="currency"
                  name="currency"
                  isClearable={true}
                  options={currencyData?.data?.data?.content || []}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  getOptionValue={(option) => option.code}
                  isSearchable={true}
                  placeholder="Select Currency"
                  value={currencyData?.data?.data?.content?.find((item) => item?.code === values?.currency) || ''}
                  handleChange={(selected) => setFieldValue("currency", selected?.code ?? '')}
                  handleBlur={handleBlur}
                />
                <div style={{ minHeight: "18px" }}>
                  {touched?.currency && errors?.currency && (
                    <p className="requiredText">
                      {errors?.currency}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-md-3 mb-2">
                <label className="fontSize14 menacingCloudsText fontWeight500 mb-1">
                  Charges <span className='bloodDonortext fontWeight600 fontSize16'>*</span>
                </label>
                <input
                  placeholder="Charges"
                  className="mainInput"
                  name="charges"
                  value={values?.charges}
                  onChange={(e) => setFieldValue("charges", e?.target?.value)}
                  onBlur={handleBlur}
                />
                <div style={{ minHeight: "18px" }}>
                  {touched?.charges && errors?.charges && (
                    <p className="requiredText">
                        {errors?.charges}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row mb_22" style={{ marginTop: "40px" }}>
              <div className="col-md-12 displayFlex alignItemCenter justifyContent_end gap_16">
                  <button type="button" onClick={handleSubmit} className="blue_btn">
                    Save
                  </button>
                  <button className="cancel_btn" onClick={onCancel}>Cancel</button>
              </div>
          </div>
      </div>
    </div>
  );
};

export default BankChargesForm;